// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, FormControlLabel, Switch } from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { DateUtils, StringUtils } from '../../../../../common/utils';
import { DataTable, EmptyState } from '../../../../../common/components';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  switch: {
    padding: theme.spacing(1),
  },
  red: {
    color: theme.palette.error.main,
  },
}));

function OrderList(props) {
  const [t] = useTranslation();
  const classes = useStyles();

  const { data, showActive, onSelectOrder, onFetchData, onFilterChange, actionsDisabled, getSum } = props;

  const getColumnDefs = () => [
    {
      field: 'orderId',
      headerName: t('order.orderId'),
      width: 130,
    },
    {
      field: 'productId',
      headerName: t('product.productId'),
      width: 145,
    },
    {
      field: 'productName',
      headerName: t('product.productName'),
      width: 250,
    },
    {
      field: 'campaign',
      headerName: t('order.orderCampaign'),
      width: 150,
      valueGetter: params => params.row.voucher ? `G: ${params.row.voucher}` : params.row.promotion ? `P: ${params.row.promotion}` : "",
    },
    {
      field: 'sum',
      headerName: t('order.orderSum'),
      width: 125,
      valueGetter: params => getSum(params.row),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'discount',
      headerName: t('order.orderDiscount'),
      width: 125,
      valueGetter: params => params.row.type === 'DISCOUNT_PERCENT' ? `${params.row.sum} %` : params.row.type === 'DISCOUNT_AMOUNT' ? StringUtils.getFormattedSum(params.row.sum) : "",
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'duration',
      headerName: t('order.orderDuration'),
      width: 150,
      valueGetter: params => StringUtils.getFormattedDays(params.value),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'freePeriod',
      headerName: t('order.orderFreePeriod'),
      width: 170,
      valueGetter: params => StringUtils.getFormattedDays(params.value),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'creationDate',
      headerName: t('order.orderCreated'),
      width: 175,
      type: 'date',
      valueFormatter: params => DateUtils.getDate(params.value),
    },
    {
      field: 'effectiveDate',
      headerName: t('order.orderEffectiveDate'),
      width: 175,
      type: 'date',
      valueFormatter: params => DateUtils.getDate(params.value),
    },
    {
      field: 'type',
      headerName: t('order.orderType'),
      width: 150,
      valueFormatter: params =>
        StringUtils.getEnumTranslation(t, 'order.orderTypeEnum', params.value, params.value),
    },
    {
      field: 'status',
      headerName: t('order.orderStatus'),
      width: 150,
      valueFormatter: params =>
        StringUtils.getEnumTranslation(t, 'order.orderStatusEnum', params.value, params.value),
    },
    {
      field: 'retryCount',
      headerName: t('order.orderRetryCount'),
      width: 155,
    },
    {
      field: 'lastGrace',
      headerName: t('order.orderLastGrace'),
      width: 160,
      filterable: false,
      valueFormatter: params => (params.value ? 'Ja' : 'Nej'),
    },
  ];

  const getRowData = () =>
    data.content.map(order => ({
      ...order,
      id: order.orderId,
      productId: order.product.id,
      productName: order.product.title,
    }));

  const renderToolbar = () => (
    <GridToolbarContainer>
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="OrderList-showActive"
            color="primary"
            checked={showActive}
            onChange={onFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('common.onlyActive')}
      />
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('order.ordersEmpty')} />
    </GridOverlay>
  );

  return (
    <Box id="OrderList-container" className={classes.container}>
      <DataTable
        id="OrderList-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        rowCount={data.total}
        pageSize={data.size}
        pageIndex={data.page - 1}
        onRowClick={onSelectOrder}
        onPageChange={onFetchData}
        disableRowClick={actionsDisabled}
        paginationMode="server"
        pagination
      />
    </Box>
  );
}

OrderList.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number,
    size: PropTypes.number,
    total: PropTypes.number,
    content: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  showActive: PropTypes.bool.isRequired,
  onSelectOrder: PropTypes.func.isRequired,
  onFetchData: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
  getSum: PropTypes.func.isRequired,
};

export default OrderList;
